import { useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuCloseIcon, MenuImageAddIcon } from '@noor/ui';
import { MAX_IMAGE_BYTES, MAX_IMAGE_LABEL, toast } from '@noor/lib';
import type { BranchFormPhoto } from '../model/schema';

interface PhotoFieldProps {
  /** Form field this tile owns — `icon` or `backgroundImage`. */
  name: 'icon' | 'backgroundImage';
  label: string;
  /** Prompt inside the empty drop zone («Загрузить фото»). */
  uploadLabel: string;
  /** Format requirements, surfaced as the tile's tooltip. */
  hint: string;
  removeLabel: string;
  required?: boolean;
  /** Width class — the design gives the two tiles different widths. */
  className?: string;
}

/**
 * One of the branch's two photos (Figma «Image uploader», 840:34935): a 139px
 * DASHED drop zone on a grey field, prompting with an image-add glyph over
 * «Загрузить фото», and swapping to the photo itself once one is picked.
 *
 * The format requirements ride as the tile's `title` rather than as body text
 * beside it: the design's row puts the two tiles side by side with no room for
 * a paragraph, and the constraints only matter at the moment of picking.
 */
export function PhotoField({
  name,
  label,
  uploadLabel,
  hint,
  removeLabel,
  required = false,
  className,
}: PhotoFieldProps) {
  const { t } = useTranslation('eats');
  const { control, setValue } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const photo = field.value as BranchFormPhoto | undefined;

  const inputRef = useRef<HTMLInputElement>(null);
  // Every object URL this field created, revoked together on unmount. Revoking
  // eagerly on replace would blank the tile still rendering that exact URL.
  const createdUrls = useRef<Set<string>>(new Set());
  useEffect(() => {
    const created = createdUrls.current;
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
      created.clear();
    };
  }, []);

  function pick(files: FileList | null): void {
    const file = files?.[0];
    if (!file) return;
    // Rejected by NAME rather than dropped silently — a photo that vanishes on
    // selection reads as a broken uploader. The input is cleared either way, so
    // re-picking the same file after shrinking it still fires `change`.
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(
        t('menu.product.form.photoTooLarge', {
          name: file.name,
          limit: MAX_IMAGE_LABEL,
        }),
      );
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const url = URL.createObjectURL(file);
    createdUrls.current.add(url);
    field.onChange({ url, file } satisfies BranchFormPhoto);
    if (inputRef.current) inputRef.current.value = '';
  }

  // Figma 840:35136: 6px from the label to the tile, matching every other
  // labelled control in the modal.
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <span className="px-1 text-sm font-medium tracking-tight text-foreground">
        {label} {required && <span className="text-info">*</span>}
      </span>

      <div className="relative h-[139px]">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          title={hint}
          className="size-full overflow-hidden rounded-3xl border border-dashed border-input bg-muted transition-colors outline-none hover:cursor-pointer hover:bg-muted/70 focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          {photo ? (
            <img src={photo.url} alt="" className="size-full object-cover" />
          ) : (
            /* 9px between glyph and prompt (Figma «Image upload content»). */
            <span className="flex size-full flex-col items-center justify-center gap-[9px] text-foreground">
              <MenuImageAddIcon className="h-5 w-[22px]" />
              <span className="text-xs font-medium">{uploadLabel}</span>
            </span>
          )}
        </button>

        {photo && (
          <button
            type="button"
            onClick={() =>
              setValue(name, undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              })
            }
            aria-label={removeLabel}
            className="absolute -top-1.5 -right-2 flex size-6 items-center justify-center rounded-full bg-card text-foreground shadow-[0_0_16px_rgba(0,0,0,0.12)] transition-colors outline-none hover:cursor-pointer hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40"
          >
            <MenuCloseIcon className="size-[18px]" />
          </button>
        )}
      </div>
      {fieldState.error?.message && (
        <span role="alert" className="px-1 text-xs text-destructive">
          {fieldState.error.message}
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept="image/jpeg,image/jpg"
        onChange={(e) => pick(e.target.files)}
      />
    </div>
  );
}
